import { type EditablePurchaseOrderItemForm, ItemGrid, emptyItemRow, serializeItems } from '@/components/features/PurchaseOrders/ItemGrid';
import { CompanySelect } from '@/components/global/CompanySelect';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import CreateLayout from '@/layouts/create-layout';
import { type BreadcrumbItem, type PageProps } from '@/types';
import type { PurchaseOrderItemCategory } from '@/types/purchase-order';
import { router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface ProjectOption {
    id: string;
    uuid: string;
    contract_name: string;
    contract_number: string;
}

interface LeadOption {
    id: string;
    uuid: string;
    lead_number: string | null;
}

interface CreateProps extends PageProps {
    [key: string]: unknown;
    projects: ProjectOption[];
    leads: LeadOption[];
    categories: PurchaseOrderItemCategory[];
}

type PurposeType = 'warehouse' | 'project' | 'lead';

function formatCurrency(amount: number, currency: string): string {
    return new Intl.NumberFormat('lt-LT', { style: 'currency', currency: currency || 'EUR' }).format(amount);
}

export default function PurchaseOrderCreate() {
    const { props } = usePage<CreateProps>();
    const { projects, leads, categories } = props;
    const { t } = useTranslations();

    const [supplierCompanyId, setSupplierCompanyId] = useState('');
    const [purpose, setPurpose] = useState<PurposeType>('warehouse');
    const [projectId, setProjectId] = useState('');
    const [leadId, setLeadId] = useState('');
    const [currency, setCurrency] = useState('EUR');
    const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
    const [expectedDelivery, setExpectedDelivery] = useState('');
    const [notes, setNotes] = useState('');
    const [items, setItems] = useState<EditablePurchaseOrderItemForm[]>([emptyItemRow(), emptyItemRow()]);
    const [submitting, setSubmitting] = useState(false);

    const breadcrumbs: BreadcrumbItem[] = [
        { title: t('dashboard.title', 'Dashboard'), href: '/' },
        { title: t('purchase_orders.index.title', 'Purchase Orders'), href: route('purchase-orders.index') },
        { title: t('purchase_orders.form.create_title', 'New Purchase Order'), href: '#' },
    ];

    const projectOptions = projects.map((p) => ({
        value: p.uuid,
        label: `${p.contract_name} (${p.contract_number})`,
    }));

    const leadOptions = leads.map((l) => ({
        value: l.uuid,
        label: l.lead_number ?? l.uuid,
    }));

    const subtotal = items.reduce((sum, i) => sum + (parseFloat(i.qty_ordered) || 0) * (parseFloat(i.unit_price) || 0), 0);

    const handleSubmit = () => {
        if (!supplierCompanyId) {
            toast.error(t('toasts.purchase_orders_create.select_supplier', 'Please select a supplier.'));
            return;
        }
        const validItems = serializeItems(items);
        if (validItems.length === 0) {
            toast.error(t('toasts.purchase_orders_create.add_line_item', 'Add at least one complete line item (description, qty, price).'));
            return;
        }
        setSubmitting(true);
        router.post(
            route('purchase-orders.store'),
            {
                supplier_company_id: supplierCompanyId,
                lead_id: purpose === 'lead' ? leadId || null : null,
                project_id: purpose === 'project' ? projectId || null : null,
                currency,
                order_date: orderDate,
                expected_delivery_date: expectedDelivery || null,
                notes: notes || null,
                items: validItems,
            } as never,
            {
                onError: (errors) => {
                    console.error(errors);
                    toast.error(t('toasts.purchase_orders_create.create_failed', 'Failed to create purchase order. Check all fields.'));
                    setSubmitting(false);
                },
            },
        );
    };

    const sectionNum = (n: number) => (
        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
            {n}
        </span>
    );

    return (
        <CreateLayout
            breadcrumbs={breadcrumbs}
            title={t('purchase_orders.form.create_title', 'New Purchase Order')}
            subtitle={t('purchase_orders.form.create_subtitle', 'Fill in supplier, dates, and line items to create a purchase order.')}
        >
            <div className="space-y-6 pb-6">
                {/* Section 1 — Supplier & Purpose */}
                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            {sectionNum(1)}
                            <div className="space-y-1">
                                <CardTitle>{t('purchase_orders.form.sections.supplier', 'Supplier & Purpose')}</CardTitle>
                                <CardDescription>
                                    {t('purchase_orders.form.sections.supplier_desc', 'Who are you ordering from, and what is this order for?')}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">
                                {t('purchase_orders.form.supplier', 'Supplier')} <span className="text-destructive">*</span>
                            </Label>
                            <CompanySelect
                                value={supplierCompanyId}
                                onValueChange={setSupplierCompanyId}
                                placeholder={t('purchase_orders.form.select_supplier', 'Search supplier...')}
                                allowCreate={false}
                            />
                        </div>

                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">{t('purchase_orders.form.purpose', 'Purpose')}</Label>
                            <div className="flex gap-2">
                                {(['warehouse', 'project', 'lead'] as PurposeType[]).map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => setPurpose(p)}
                                        className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${purpose === p ? 'border-primary/40 bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
                                    >
                                        {p === 'warehouse' && t('purchase_orders.form.purpose_warehouse', 'Warehouse / Stock')}
                                        {p === 'project' && t('purchase_orders.form.purpose_project', 'Project')}
                                        {p === 'lead' && t('purchase_orders.form.purpose_lead', 'Lead')}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {purpose === 'project' && (
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t('purchase_orders.form.project', 'Project')}</Label>
                                <Combobox
                                    options={projectOptions}
                                    value={projectId}
                                    onValueChange={(v) => setProjectId(v)}
                                    placeholder={t('purchase_orders.form.select_project', 'Search projects...')}
                                    searchPlaceholder="Type to search..."
                                    emptyText="No projects found."
                                    allowClear
                                    clearText="No project"
                                    requireSearchQuery={projectOptions.length > 10}
                                    typeToSearchHint="Type to search projects..."
                                />
                            </div>
                        )}

                        {purpose === 'lead' && (
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t('purchase_orders.form.lead', 'Lead')}</Label>
                                <Combobox
                                    options={leadOptions}
                                    value={leadId}
                                    onValueChange={(v) => setLeadId(v)}
                                    placeholder={t('purchase_orders.form.select_lead', 'Search leads...')}
                                    searchPlaceholder="Type to search..."
                                    emptyText="No leads found."
                                    allowClear
                                    clearText="No lead"
                                    requireSearchQuery={leadOptions.length > 10}
                                    typeToSearchHint="Type to search leads..."
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Section 2 — Dates & Currency */}
                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            {sectionNum(2)}
                            <div className="space-y-1">
                                <CardTitle>{t('purchase_orders.form.sections.dates', 'Dates & Currency')}</CardTitle>
                                <CardDescription>
                                    {t('purchase_orders.form.sections.dates_desc', 'When was the order placed and when do you expect delivery?')}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-3">
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">
                                    {t('purchase_orders.form.order_date', 'Order Date')} <span className="text-destructive">*</span>
                                </Label>
                                <DatePicker value={orderDate} onValueChange={(v) => setOrderDate(v)} placeholder="Pick a date" />
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">
                                    {t('purchase_orders.form.expected_delivery', 'Expected Delivery')}
                                </Label>
                                <DatePicker value={expectedDelivery} onValueChange={(v) => setExpectedDelivery(v)} placeholder="Pick a date" />
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t('purchase_orders.form.currency', 'Currency')}</Label>
                                <Select value={currency} onValueChange={setCurrency}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {['EUR', 'USD', 'GBP', 'PLN', 'SEK', 'NOK', 'DKK', 'CHF', 'CZK'].map((c) => (
                                            <SelectItem key={c} value={c}>
                                                {c}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Section 3 — Line Items */}
                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            {sectionNum(3)}
                            <div className="space-y-1">
                                <CardTitle>{t('purchase_orders.show.line_items.title', 'Line Items')}</CardTitle>
                                <CardDescription>
                                    {t(
                                        'purchase_orders.form.sections.items_desc',
                                        'Tab or Enter to move between rows. Paste from Excel to import multiple rows at once.',
                                    )}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <ItemGrid
                            items={items}
                            onChange={setItems}
                            currency={currency}
                            categories={categories}
                            canRemoveRows
                            showStatus={false}
                            t={t}
                        />
                    </CardContent>
                </Card>

                {/* Section 4 — Notes */}
                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            {sectionNum(4)}
                            <div className="space-y-1">
                                <CardTitle>{t('purchase_orders.form.sections.notes', 'Notes')}</CardTitle>
                                <CardDescription>
                                    {t(
                                        'purchase_orders.form.sections.notes_desc',
                                        'Delivery instructions, special requirements, or internal remarks.',
                                    )}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t('purchase_orders.form.notes_placeholder', 'Delivery instructions, special notes...')}
                            rows={3}
                        />
                    </CardContent>
                </Card>

                {/* Sticky footer */}
                <div className="bg-background/95 sticky bottom-0 z-10 flex items-center justify-between gap-4 rounded-md border p-4 backdrop-blur">
                    <span className="text-sm font-semibold">
                        {t('purchase_orders.show.order_summary.total', 'Total')}:{' '}
                        <span className="tabular-nums">{formatCurrency(subtotal, currency)}</span>
                    </span>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => router.visit(route('purchase-orders.index'))}>
                            {t('purchase_orders.form.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={handleSubmit} disabled={submitting}>
                            {submitting ? 'Creating...' : t('purchase_orders.form.save', 'Save Purchase Order')}
                        </Button>
                    </div>
                </div>
            </div>
        </CreateLayout>
    );
}
